/**
 * Routing Rules Page
 * 
 * Professional CRUD page for managing SIP routing rules and SIP request handling.
 * Follows the same pattern as CertificatesPage for UI consistency.
 */

import React, { useMemo, useState } from 'react';
import { 
  GitBranch,
  Plus,
  CheckCircle,
  XCircle,
  ShieldCheck,
  Info,
  Settings,
  Target
} from 'lucide-react';
import { 
  useRoutingRules, 
  useToggleRoutingRule, 
  useDeleteRoutingRule,
} from '../../hooks/useRouting';
import { useSipPeers } from '../../hooks/useSipPeers';
import { usePeerGroups } from '../../hooks/usePeerGroups';
import { useSipManipulationsForDropdown } from '../../hooks/useSipManipulations';
import { RoutingRuleEditor } from '../../components/routing/routing-rule-editor/RoutingRuleEditor';
import { TemplatePicker } from '../../components/routing/routing-rule-editor/TemplatePicker';
import type { TemplateKey } from '../../components/routing/routing-rule-editor/schema';
import type { Sip } from '@sbclite/core';
import { useViewMode } from '../../hooks/useViewMode';
import { formatFlowSummary } from './routingFlowSummary';

// Common components
import {
  PageHeader,
  PageContainer,
  StatsGrid,
  StatCard,
  FilterBar,
  ItemCard,
  ItemCardIcon,
  ItemActions,
  EmptyState,
  LoadingState,
  ErrorState,
  StatusBadge,
  DeleteConfirmModal,
  TabbedDetailsModal,
  DetailSection,
  DetailGrid,
  DetailItem,
  type Tab,
  type FilterOption,
} from '../../components/common';

// ========================================
// TYPES
// ========================================

type FilterType = 'all' | 'enabled' | 'disabled' | 'default-forwarding' | 'custom-forwarding';

// ========================================
// CONSTANTS
// ========================================

const FILTER_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All Rules' },
  { value: 'enabled', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'default-forwarding', label: 'Default SIP handling' },
  { value: 'custom-forwarding', label: 'Custom SIP handling' },
];

const DETAILS_TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'conditions', label: 'Conditions', icon: Settings },
  { id: 'actions', label: 'Actions', icon: Target },
];

// ========================================
// HELPER FUNCTIONS
// ========================================

function usesDefaultForwardingHandling(rule: Sip.RoutingRuleInfo): boolean {
  return rule.recordRouteEnabled !== false && 
         rule.decrementMaxForwards !== false;
}

function getPriorityLabel(priority: number): string {
  if (priority <= 10) return 'Critical';
  if (priority <= 30) return 'High';
  if (priority <= 60) return 'Medium';
  return 'Low';
}

function getPriorityColor(priority: number): string {
  if (priority <= 10) return 'text-red-600';
  if (priority <= 30) return 'text-orange-600';
  if (priority <= 60) return 'text-yellow-600';
  return 'text-green-600';
}

// ========================================
// MAIN COMPONENT
// ========================================

export default function RoutingRulesPage() {
  // State management
  const [viewMode, setViewMode] = useViewMode();
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [templateKey, setTemplateKey] = useState<TemplateKey | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState<Sip.RoutingRuleInfo | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<Sip.RoutingRuleInfo | null>(null);
  const [editingRule, setEditingRule] = useState<Sip.RoutingRuleInfo | null>(null);
  const [detailsActiveTab, setDetailsActiveTab] = useState('overview');

  // API queries
  const { data: response, isLoading, error, refetch, isFetching } = useRoutingRules();
  const rules: Sip.RoutingRuleInfo[] = response?.data || [];
  
  // Fetch peers and groups to resolve names from UUIDs
  const { data: peersResponse } = useSipPeers();
  const { data: groupsResponse } = usePeerGroups();
  const { data: manipulationsResponse } = useSipManipulationsForDropdown();
  
  const peers = peersResponse?.data || [];
  const peerGroups = groupsResponse?.data || [];
  const manipulationNameById = useMemo(
    () => new Map((manipulationsResponse?.data ?? []).map((manipulation) => [manipulation.id, manipulation.name] as const)),
    [manipulationsResponse?.data],
  );

  // Mutations
  const toggleMutation = useToggleRoutingRule();
  const deleteMutation = useDeleteRoutingRule();
  // ========================================
  // HANDLERS
  // ========================================

  const handleCreate = () => {
    setEditingRule(null);
    setTemplateKey(null);
    setShowTemplatePicker(true);
  };

  const handleTemplateSelected = (key: TemplateKey) => {
    setTemplateKey(key);
    setEditingRule(null);
    setShowTemplatePicker(false);
    setIsEditorOpen(true);
  };

  const handleEdit = (rule: Sip.RoutingRuleInfo) => {
    setEditingRule(rule);
    setTemplateKey(null);
    setIsEditorOpen(true);
  };

  const handleEditorClose = () => {
    setIsEditorOpen(false);
    setEditingRule(null);
    setTemplateKey(null);
  };

  const handleViewDetails = (rule: Sip.RoutingRuleInfo) => {
    setSelectedRule(rule);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (rule: Sip.RoutingRuleInfo) => {
    setRuleToDelete(rule);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (ruleToDelete) {
      deleteMutation.mutate(ruleToDelete.id, {
        onSuccess: () => {
          setShowDeleteModal(false);
          setRuleToDelete(null);
        }
      });
    }
  };

  const handleToggle = (rule: Sip.RoutingRuleInfo) => {
    toggleMutation.mutate(rule.id);
  };

  const handleDuplicate = (rule: Sip.RoutingRuleInfo) => {
    const duplicateRule: Sip.RoutingRuleInfo = {
      ...rule,
      name: `${rule.name} (Copy)`,
      id: `new_${Date.now()}`,
    };
    setEditingRule(duplicateRule);
    setTemplateKey(null);
    setIsEditorOpen(true);
  };

  // ========================================
  // HELPER METHODS
  // ========================================

  const getPeerDisplayName = (peerId: string | null | undefined): string => {
    if (!peerId) return '-';
    return peers.find((peer) => peer.id === peerId)?.name ?? 'Peer unavailable';
  };

  const getPeerGroupDisplayName = (peerGroupId: string | null | undefined): string => {
    if (!peerGroupId) return '-';
    return peerGroups.find((group) => group.id === peerGroupId)?.name ?? 'Peer group unavailable';
  };

  const getTargetDisplayName = (rule: Sip.RoutingRuleInfo): string => {
    if (!rule.destinationTarget) return '-';
    
    if (rule.destinationType === 'reject') {
      const rejectCodes: Record<string, string> = {
        '403': '403 Forbidden',
        '404': '404 Not Found',
        '486': '486 Busy Here',
        '503': '503 Service Unavailable',
      };
      return rejectCodes[rule.destinationTarget] || rule.destinationTarget;
    }
    
    if (rule.destinationType === 'peer' || rule.destinationType === 'sip_peer') {
      return getPeerDisplayName(rule.destinationTarget);
    }
    
    if (rule.destinationType === 'peerGroup' || rule.destinationType === 'peer_group') {
      return getPeerGroupDisplayName(rule.destinationTarget);
    }
    
    return rule.destinationTarget;
  };

  const getManipulationDisplayName = (manipulationId: string | null | undefined): string => {
    if (!manipulationId) return 'None';
    return manipulationNameById.get(manipulationId) ?? 'Unknown manipulation profile';
  };

  // ========================================
  // HELPER: Check if rule is enabled
  // ========================================
  
  const isEnabled = (rule: Sip.RoutingRuleInfo): boolean => {
    return Boolean(rule.enabled);
  };

  // ========================================
  // FILTERING
  // ========================================

  const filteredRules = rules.filter(rule => {
    // Filter by type
    let matchesFilter = false;
    switch (filterType) {
      case 'all':
        matchesFilter = true;
        break;
      case 'enabled':
        matchesFilter = isEnabled(rule);
        break;
      case 'disabled':
        matchesFilter = !isEnabled(rule);
        break;
      case 'default-forwarding':
        matchesFilter = usesDefaultForwardingHandling(rule);
        break;
      case 'custom-forwarding':
        matchesFilter = !usesDefaultForwardingHandling(rule);
        break;
    }

    // Filter by search
    const matchesSearch = searchQuery === '' ||
      rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (rule.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

    return matchesFilter && matchesSearch;
  });

  const sortedRules = [...filteredRules].sort((a, b) => a.priority - b.priority);

  // ========================================
  // STATISTICS
  // ========================================

  const stats = {
    total: rules.length,
    enabled: rules.filter(r => isEnabled(r)).length,
    disabled: rules.filter(r => !isEnabled(r)).length,
    defaultForwarding: rules.filter(r => usesDefaultForwardingHandling(r)).length,
  };

  // ========================================
  // LOADING & ERROR STATES
  // ========================================

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorState
        title="Error loading routing rules"
        message={error instanceof Error ? error.message : 'An unexpected error occurred'}
        onRetry={() => refetch()}
      />
    );
  }

  // ========================================
  // RENDER
  // ========================================

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader
        title="Routing Rules"
        description="Configure call routing logic and SIP request handling"
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
        primaryAction={{
          label: 'Add Rule',
          icon: Plus,
          onClick: handleCreate,
        }}
      />

      {/* Statistics Cards */}
      <StatsGrid>
        <StatCard
          label="Total Rules"
          value={stats.total}
          icon={GitBranch}
          iconBgColor="bg-blue-100"
          iconColor="text-blue-600"
        />
        <StatCard
          label="Enabled"
          value={stats.enabled}
          icon={CheckCircle}
          iconBgColor="bg-green-100"
          iconColor="text-green-600"
          valueColor="text-green-600"
        />
        <StatCard
          label="Disabled"
          value={stats.disabled}
          icon={XCircle}
          iconBgColor="bg-gray-100"
          iconColor="text-gray-600"
        />
        <StatCard
          label="Default SIP handling"
          value={`${stats.defaultForwarding} / ${stats.total}`}
          icon={ShieldCheck}
          iconBgColor="bg-purple-100"
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />
      </StatsGrid>

      {/* Filters and Search */}
      <FilterBar
        searchPlaceholder="Search routing rules..."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        filterOptions={FILTER_OPTIONS}
        filterValue={filterType}
        onFilterChange={(value) => setFilterType(value as FilterType)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Rules Display */}
      {sortedRules.length === 0 ? (
        <EmptyState
          icon={GitBranch}
          title="No routing rules found"
          description="Get started by creating your first routing rule to control call flow."
          isFiltered={searchQuery !== '' || filterType !== 'all'}
          action={{
            label: 'Add Rule',
            icon: Plus,
            onClick: handleCreate,
          }}
        />
      ) : viewMode === 'grid' ? (
        // Grid View
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {sortedRules.map((rule) => {
            const ruleEnabled = isEnabled(rule);
            const defaultForwarding = usesDefaultForwardingHandling(rule);

            return (
              <ItemCard
                key={rule.id}
                icon={
                  <ItemCardIcon 
                    icon={<GitBranch className="w-5 h-5 text-blue-600" />} 
                    bgColor="bg-blue-100" 
                  />
                }
                title={rule.name}
                subtitle={formatFlowSummary(rule, peers, peerGroups)}
                status={
                  <div className="flex items-center gap-2">
                    <StatusBadge status={ruleEnabled ? 'enabled' : 'disabled'} />
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      defaultForwarding ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {defaultForwarding ? 'Default' : 'Custom'}
                    </span>
                  </div>
                }
                details={[
                  { 
                    label: 'Priority', 
                    value: (
                      <span className={getPriorityColor(rule.priority)}>
                        {rule.priority} ({getPriorityLabel(rule.priority)})
                      </span>
                    ) 
                  },
                ]}
                actions={
                  <ItemActions
                    variant="card"
                    actions={[
                      {
                        kind: ruleEnabled ? 'disable' : 'enable',
                        onClick: () => handleToggle(rule),
                        disabled: toggleMutation.isPending,
                      },
                      { kind: 'view', onClick: () => handleViewDetails(rule) },
                      { kind: 'edit', onClick: () => handleEdit(rule) },
                      { kind: 'duplicate', onClick: () => handleDuplicate(rule) },
                      { kind: 'delete', onClick: () => handleDeleteClick(rule) },
                    ]}
                  />
                }
              />
            );
          })}
        </div>
      ) : (
        // List View (Table)
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rule</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Forwarding handling</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRules.map((rule) => {
                const ruleEnabled = isEnabled(rule);
                const defaultForwarding = usesDefaultForwardingHandling(rule);

                return (
                  <tr key={rule.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-gray-900">{rule.name}</div>
                        <div className="text-xs text-gray-500">{formatFlowSummary(rule, peers, peerGroups)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getPriorityColor(rule.priority)}`}>
                        {rule.priority} ({getPriorityLabel(rule.priority)})
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        defaultForwarding ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {defaultForwarding ? 'Default' : 'Custom'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={ruleEnabled ? 'enabled' : 'disabled'} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <ItemActions
                        variant="table"
                        actions={[
                          {
                            kind: ruleEnabled ? 'disable' : 'enable',
                            onClick: () => handleToggle(rule),
                            disabled: toggleMutation.isPending,
                          },
                          { kind: 'view', title: 'View Details', onClick: () => handleViewDetails(rule) },
                          { kind: 'edit', onClick: () => handleEdit(rule) },
                          { kind: 'duplicate', onClick: () => handleDuplicate(rule) },
                          { kind: 'delete', onClick: () => handleDeleteClick(rule) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create flow: template picker → editor */}
      <TemplatePicker
        isOpen={showTemplatePicker}
        onClose={() => setShowTemplatePicker(false)}
        onSelect={handleTemplateSelected}
      />

      {/* Routing Rule Editor (tabbed modal) */}
      <RoutingRuleEditor
        isOpen={isEditorOpen}
        onClose={handleEditorClose}
        onSuccess={() => {
          refetch();
        }}
        editingRule={editingRule}
        templateKey={templateKey}
      />

      {/* Details Modal */}
      {selectedRule && (
        <TabbedDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedRule(null);
            setDetailsActiveTab('overview');
          }}
          title={selectedRule.name}
          subtitle={selectedRule.description || 'Routing Rule'}
          tabs={DETAILS_TABS}
          activeTab={detailsActiveTab}
          onTabChange={setDetailsActiveTab}
        >
          <>
            {detailsActiveTab === 'overview' && (
                <DetailSection title="Overview">
                  <DetailGrid columns={2}>
                    <DetailItem label="Name" value={selectedRule.name} />
                    <DetailItem label="Priority" value={
                      <span className={getPriorityColor(selectedRule.priority)}>
                        {selectedRule.priority} ({getPriorityLabel(selectedRule.priority)})
                      </span>
                    } />
                    <DetailItem label="Status" value={
                      <StatusBadge status={selectedRule.enabled ? 'enabled' : 'disabled'} />
                    } />
                    <DetailItem label="Forwarding handling" value={
                      usesDefaultForwardingHandling(selectedRule) ? 'Default' : 'Customized'
                    } />
                    <DetailItem label="Description" value={selectedRule.description || '-'} />
                    <DetailItem label="Created" value={
                      selectedRule.createdAt ? new Date(selectedRule.createdAt).toLocaleString() : '-'
                    } />
                  </DetailGrid>
                </DetailSection>
              )}

            {detailsActiveTab === 'conditions' && (
                <>
                <DetailSection title="Matching Conditions">
                  <DetailGrid columns={2}>
                    <DetailItem label="Source Scope" value={selectedRule.sourceScope || 'any'} />
                    <DetailItem label="Source Type" value={selectedRule.sourceType || 'any'} />
                    <DetailItem label="Source Peer" value={getPeerDisplayName(selectedRule.sourcePeerId)} />
                    <DetailItem label="Source Peer Group" value={getPeerGroupDisplayName(selectedRule.sourcePeerGroupId)} />
                    <DetailItem label="Source Pattern" value={selectedRule.sourcePattern || '*'} />
                    {selectedRule.conditions?.fromPattern && (
                      <DetailItem label="From Pattern" value={selectedRule.conditions.fromPattern} />
                    )}
                    {selectedRule.conditions?.toPattern && (
                      <DetailItem label="To Pattern" value={selectedRule.conditions.toPattern} />
                    )}
                    {selectedRule.conditions?.userAgent && (
                      <DetailItem label="User Agent" value={selectedRule.conditions.userAgent} />
                    )}
                    {selectedRule.conditions?.timeWindow && (
                      <>
                        <DetailItem label="Time Start" value={selectedRule.conditions.timeWindow.start} />
                        <DetailItem label="Time End" value={selectedRule.conditions.timeWindow.end} />
                        <DetailItem label="Timezone" value={selectedRule.conditions.timeWindow.timezone || 'UTC'} />
                      </>
                    )}
                  </DetailGrid>
                </DetailSection>

                {(Boolean(selectedRule.routeHeaders?.length) || Object.keys(selectedRule.rfcConditions ?? {}).length > 0) && (
                  <DetailSection title="Advanced SIP routing conditions">
                    <p className="mb-3 text-sm text-gray-500">
                      These conditions match incoming Route and Record-Route headers and request hop limits.
                    </p>
                    <DetailGrid columns={2}>
                      {selectedRule.rfcConditions?.requireRoute !== undefined && (
                        <DetailItem label="Require Route header" value={selectedRule.rfcConditions.requireRoute ? 'Yes' : 'No'} />
                      )}
                      {selectedRule.rfcConditions?.routePattern && (
                        <DetailItem label="Route URI pattern" value={selectedRule.rfcConditions.routePattern} />
                      )}
                      {selectedRule.rfcConditions?.maxForwardsMin !== undefined && (
                        <DetailItem label="Minimum Max-Forwards" value={String(selectedRule.rfcConditions.maxForwardsMin)} />
                      )}
                      {selectedRule.rfcConditions?.requireRecordRoute !== undefined && (
                        <DetailItem label="Require Record-Route" value={selectedRule.rfcConditions.requireRecordRoute ? 'Yes' : 'No'} />
                      )}
                      {selectedRule.routeHeaders && selectedRule.routeHeaders.length > 0 && (
                        <DetailItem label="Preloaded Route headers" value={selectedRule.routeHeaders.join(', ')} />
                      )}
                    </DetailGrid>
                  </DetailSection>
                )}

                </>
              )}
            {detailsActiveTab === 'actions' && (
                <>
                  <DetailSection title="Routing Target">
                    <DetailGrid columns={2}>
                      <DetailItem label="Destination Type" value={selectedRule.destinationType || '-'} />
                      <DetailItem label="Target" value={getTargetDisplayName(selectedRule)} />
                    </DetailGrid>
                  </DetailSection>

                  <DetailSection title="Peer forwarding controls">
                    <DetailGrid columns={3}>
                      <DetailItem label="Insert Record-Route" value={
                        <StatusBadge status={selectedRule.recordRouteEnabled !== false ? 'enabled' : 'disabled'} />
                      } />
                      <DetailItem label="Decrement Max-Forwards" value={
                        <StatusBadge status={selectedRule.decrementMaxForwards !== false ? 'enabled' : 'disabled'} />
                      } />
                    </DetailGrid>
                    <p className="mt-3 text-sm text-gray-500">
                      These controls apply when forwarding to a SIP peer. Registered-contact delivery uses its own fixed outbound handling.
                    </p>
                  </DetailSection>

                  {(selectedRule.inboundManipulationId || selectedRule.outboundManipulationId) && (
                    <DetailSection title="SIP Manipulations">
                      <DetailGrid columns={2}>
                        <DetailItem label="Route inbound" value={getManipulationDisplayName(selectedRule.inboundManipulationId)} />
                        <DetailItem label="Route outbound" value={getManipulationDisplayName(selectedRule.outboundManipulationId)} />
                      </DetailGrid>
                      <p className="text-sm text-gray-500">Route manipulations are applied after peer-level manipulation and before the routed request is handed off.</p>
                    </DetailSection>
                  )}
                </>
              )}
            </>
        </TabbedDetailsModal>
      )}

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setRuleToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Routing Rule"
        entityType="routing rule"
        entityName={ruleToDelete?.name || ''}
        isDeleting={deleteMutation.isPending}
      />
    </PageContainer>
  );
}
